import {useEffect, useState} from 'react'
import {useQuery} from '@tanstack/react-query'

import {useFeatureFlag} from 'hooks/useFeatureFlag'
import {BypassDataClient} from 'interfaces'
import {PoliciesService} from 'services/api'
import {
  createEmptyBypassData,
  type GetBypassQueryResponse,
  useGetBypass,
} from './useGetBypass'
import {transformUrlBypassToClientData} from './useGetUrlAndApplicationBypass'
import {getUrlBypassResourceQueryKey} from './useGetUrlBypassResource'

interface GetUrlBypassQueryProps {
  policyName: string
  enabled?: boolean
}

const useUrlBypassResourceQuery = ({
  policyName,
  enabled = true,
}: GetUrlBypassQueryProps): GetBypassQueryResponse => {
  const [bypassData, setBypassData] = useState<BypassDataClient>(
    createEmptyBypassData,
  )

  const {data, isLoading, isSuccess, isError, error, ...rest} = useQuery({
    queryKey: getUrlBypassResourceQueryKey(policyName),
    queryFn: () => PoliciesService.getUrlBypass(policyName),
    enabled,
    select: ({data}): BypassDataClient => transformUrlBypassToClientData(data),
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (!isLoading && data) {
      setBypassData(data)
    }
  }, [isLoading, data])

  return {
    data: bypassData,
    onUpdateBypassData: setBypassData,
    isLoading,
    isSuccess,
    isError,
    error,
    ...rest,
  }
}

// URL-only consumers should not depend on the Application bypass API during the migration.
export const useGetUrlBypass = ({
  policyName,
  enabled = true,
}: GetUrlBypassQueryProps): GetBypassQueryResponse => {
  const {isBypassApiMigrationEnabled} = useFeatureFlag()

  const legacyBypass = useGetBypass({
    policyName,
    enabled: enabled && !isBypassApiMigrationEnabled,
  })
  const urlBypassResource = useUrlBypassResourceQuery({
    policyName,
    enabled: enabled && isBypassApiMigrationEnabled,
  })

  return isBypassApiMigrationEnabled ? urlBypassResource : legacyBypass
}
